import React, { useEffect, useRef, useCallback } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import Comment from './Comment';

import './scss/Activities.scss';
import { fetchActivities, setHighlightTarget, resetActivities } from '../../redux/Slices/activitiesSlice';
import Spinner from '../Global/Spinner';
import { marked } from 'marked';
import BouncingDots from '../Global/BouncingDots';

import { useLocation } from 'react-router-dom';


import Play from '../icons/Play';
import Pause from '../icons/Pause';
import Stop from '../icons/Stop';
import Timer from '../icons/Timer';
import Countdown from '../icons/Countdown';
import History from '../icons/History';


const Activities = ({ cardId, isReadOnly = false, isActivityDetails }) => {

    const getTimerIcon = (actionType, details) => {
        const type = actionType.toLowerCase();
        if (type.includes('started')) {
            return details.toLowerCase().includes('countdown') ? <Countdown size={14} color="#10B981" /> : <Timer size={14} color="#10B981" />;
        }
        if (type.includes('paused')) return <Pause size={14} color="#F59E0B" />;
        if (type.includes('resumed')) return <Play size={14} color="#10B981" />;
        if (type.includes('stopped')) return <Stop size={14} color="#EF4444" />;
        if (type.includes('reset')) return <History size={14} color="var(--muted-text-color)" />;
        if (type.includes('switched')) return <Timer size={14} color="#10B981" />;
        return null;
    };

    const dispatch = useDispatch();
    const { activities, loading, loadingMore, error, highlightTarget, hasMore, page } = useSelector((state) => state.activities);
    const observerRef = useRef(null);
    const loadMoreRef = useRef(null);
    const isFetchingRef = useRef(false);
    const lastPageRef = useRef(0);
    const [isInitialized, setIsInitialized] = React.useState(false);

    // Reset activities when cardId or isActivityDetails changes
    useEffect(() => {
        dispatch(resetActivities());
        dispatch(fetchActivities({ cardId, page: 1, isActivityDetails }));
        isFetchingRef.current = false;
        lastPageRef.current = 0;
        setIsInitialized(false);

        // Set initialized after a short delay to prevent immediate observer triggers
        const timer = setTimeout(() => {
            setIsInitialized(true);
        }, 300);

        return () => clearTimeout(timer);
    }, [dispatch, cardId, isActivityDetails]);

    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const commentId = params.get('commentId');
        const replyId = params.get('replyId');
        const activityId = params.get('activityId');

        if (commentId) {
            dispatch(setHighlightTarget({ type: 'comment', id: commentId }));
        } else if (replyId) {
            dispatch(setHighlightTarget({ type: 'reply', id: replyId }));
        } else if (activityId) {
            dispatch(setHighlightTarget({ type: 'activity', id: activityId }));
        }
    }, [location.search, dispatch]);

    // Scroll to highlighted activity
    useEffect(() => {
        if (highlightTarget?.type === 'activity' && highlightTarget?.id) {
            const timer = setTimeout(() => {
                const activityElement = document.querySelector(`.activity.highlighted`);
                if (activityElement) {
                    activityElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }, 500); // Wait for activities to load and render

            return () => clearTimeout(timer);
        }
    }, [highlightTarget, activities]);

    // Reset fetching flag when loading completes
    useEffect(() => {
        if (!loading && !loadingMore) {
            isFetchingRef.current = false;
        }
    }, [loading, loadingMore]);

    // Infinite scroll callback
    const loadMore = useCallback(() => {
        // Prevent multiple simultaneous requests
        if (isFetchingRef.current || loadingMore || !hasMore || loading || !isInitialized) {
            return;
        }

        // Prevent fetching the same page multiple times
        const nextPage = page + 1;
        if (lastPageRef.current >= nextPage) {
            return;
        }

        isFetchingRef.current = true;
        lastPageRef.current = nextPage;
        dispatch(fetchActivities({ cardId, page: nextPage, isActivityDetails }));
    }, [dispatch, cardId, page, hasMore, loadingMore, loading, isActivityDetails, isInitialized]);

    // Intersection Observer for infinite scroll
    useEffect(() => {
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isFetchingRef.current) {
                    loadMore();
                }
            },
            { threshold: 0.1, rootMargin: '100px' }
        );

        if (loadMoreRef.current && hasMore) {
            observerRef.current.observe(loadMoreRef.current);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [loadMore, hasMore]);

    if (loading && activities.length === 0) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                <BouncingDots />
            </div>
        )
    }

    return (
        <div className="activities">
            {activities?.map((activity) => {
                if (!activity?.initiator?.name) return null;

                const timerIcon = getTimerIcon(activity.actionType, activity.details);

                return (
                    (activity.actionType === 'ADDED_COMMENT' || activity.actionType === 'REPLIED_TO_COMMENT') ? (
                        <Comment key={activity._id} activity={activity} cardId={cardId} isReadOnly={isReadOnly} highlightTarget={highlightTarget} />
                    ) : (
                        <div
                            key={activity._id}
                            className={`activity ${highlightTarget?.type === 'activity' && highlightTarget?.id === activity._id ? 'highlighted' : ''}`}
                        >
                            {activity?.initiator?.profilePicture ? (
                                <img
                                    src={activity.initiator.profilePicture}
                                    alt={activity.initiator.name}
                                    className="activity__profile-img"
                                    loading="lazy"
                                />
                            ) : (
                                <ImagePlaceholder className=" activity__profile-img" size={35} member={activity.initiator} text={activity.initiator.name} onlineIndicator={false} />
                            )}

                            <div className="activity__content">
                                <div className="activity__header">
                                    <strong>{activity.initiator.name}</strong>
                                    {timerIcon && <span style={{ marginRight: '6px', verticalAlign: 'middle', display: 'inline-flex' }}>{timerIcon}</span>}
                                    <div className="activity__details" dangerouslySetInnerHTML={{ __html: marked(activity.details) }} />
                                </div>

                                <span className="activity__time">
                                    {new Date(activity.createdAt).toLocaleString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: 'numeric',
                                        minute: 'numeric',
                                        second: 'numeric',
                                        hour12: true
                                    })}
                                </span>
                            </div>
                        </div>
                    )
                )
            })}


            {/* Infinite scroll trigger */}
            {hasMore && (
                <div ref={loadMoreRef} style={{ height: '20px', margin: '10px 0' }}>
                    {loadingMore && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <BouncingDots />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Activities;
